import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import ConfirmableDialog from '@/components/common/ConfirmableDialog';
import { Shield, Award, AlertTriangle, Check, X, Flame } from 'lucide-react';
import { format } from 'date-fns';

export default function ActivityPrinciplesValidation({
  activity,
  open,
  onClose,
  currentUser,
  officers,
  progressReports
}) {
  const queryClient = useQueryClient();

  const activityReports = (progressReports || []).filter(r => r?.activity_id === activity?.id).sort((a, b) => new Date(a.reported_at) - new Date(b.reported_at));
  const latestReport = activityReports[activityReports.length - 1];
  const reportedFlags = latestReport?.flagged_items || [];

  const [validatorFlags, setValidatorFlags] = useState([]);
  const [validationForm, setValidationForm] = useState({
    notes: '',
    xp_bonus_applied: 0,
    sp_bonus_applied: 0,
    xp_malus_applied: 0,
    sp_malus_applied: 0
  });

  const validateMutation = useMutation({
    mutationFn: async (data) => {
      // Update activity with validation
      await base44.entities.Activity.update(activity.id, {
        commander_principles_validation: {
          status: data.status,
          notes: data.notes,
          validated_by: currentUser?.email,
          validated_at: new Date().toISOString(),
          xp_bonus_applied: data.xp_bonus_applied,
          sp_bonus_applied: data.sp_bonus_applied,
          xp_malus_applied: data.xp_malus_applied,
          sp_malus_applied: data.sp_malus_applied,
          validator_flags: validatorFlags
        }
      });

      // Apply XP/SP changes to responsible officer
      const responsibleOfficer = officers.find((o) => o.user_email === activity.responsible_email);
      if (responsibleOfficer) {
        const newTotalXp = (responsibleOfficer.total_xp || 0) + data.xp_bonus_applied - data.xp_malus_applied;

        await base44.entities.OfficerProfile.update(responsibleOfficer.id, {
          total_xp: Math.max(0, newTotalXp)
        });

        // Apply SP changes to skills
        if (data.sp_bonus_applied > 0 || data.sp_malus_applied > 0) {
          const updatedSkills = [...(responsibleOfficer.skills || [])];
          const leadershipIndex = updatedSkills.findIndex((s) => s.name === 'Leadership');

          if (leadershipIndex >= 0) {
            const newXp = Math.max(0, (updatedSkills[leadershipIndex].xp || 0) + data.sp_bonus_applied - data.sp_malus_applied);
            const SP_PER_LEVEL = [0, 100, 250, 500, 1000, 2000];
            let newLevel = 1;
            for (let i = SP_PER_LEVEL.length - 1; i >= 0; i--) {
              if (newXp >= SP_PER_LEVEL[i]) {
                newLevel = i;
                break;
              }
            }
            updatedSkills[leadershipIndex] = {
              ...updatedSkills[leadershipIndex],
              xp: newXp,
              level: newLevel
            };
          } else {
            updatedSkills.push({
              name: 'Leadership',
              level: 1,
              xp: Math.max(0, data.sp_bonus_applied - data.sp_malus_applied)
            });
          }

          await base44.entities.OfficerProfile.update(responsibleOfficer.id, { skills: updatedSkills });
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activities'] });
      queryClient.invalidateQueries({ queryKey: ['officers'] });
      onClose();
      setValidatorFlags([]);
      setValidationForm({
        notes: '',
        xp_bonus_applied: 0,
        sp_bonus_applied: 0,
        xp_malus_applied: 0,
        sp_malus_applied: 0
      });
    }
  });

  if (!activity) return null;

  const toggleValidatorFlag = (item) => {
    if (validatorFlags.includes(item)) {
      setValidatorFlags(validatorFlags.filter(f => f !== item));
    } else {
      setValidatorFlags([...validatorFlags, item]);
    }
  };

  const allPrinciples = activity?.principles_adhered || [];
  const applicableRedLines = (activity?.red_line_considerations || [])
    .filter(rl => rl.status !== 'not_applicable')
    .map(rl => rl.red_line);
  const allItems = [...allPrinciples, ...applicableRedLines];

  const flagsMatch = 
    validatorFlags.length === reportedFlags.length &&
    validatorFlags.every(f => reportedFlags.includes(f));

  const handleSubmit = (e) => {
    e.preventDefault();
    validateMutation.mutate({
      ...validationForm,
      status: flagsMatch ? 'validated' : 'disputed'
    });
  };

  return (
    <ConfirmableDialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="cmdr-activityprinciplesvalidation-dialog" className="bg-slate-900 border-slate-800 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-400" />
            Validate Principles Adherence
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* Show what officer reported */}
          <div className="p-4 bg-slate-950 rounded-lg border border-slate-800">
            <h4 className="text-sm font-semibold text-slate-300 mb-3">Officer's Latest Report</h4>
            {latestReport ? (
              <>
                <div className="text-xs text-slate-400 mb-3">
                  Reported by {latestReport.reported_by} on {latestReport.reported_at && format(new Date(latestReport.reported_at), 'MMM d, yyyy')}
                </div>
                {latestReport.narrative_on_principles && (
                  <div className="mb-3">
                    <Label className="text-xs text-slate-400 mb-1 block">Narrative</Label>
                    <p className="text-sm text-slate-300 bg-slate-900/50 p-2 rounded">{latestReport.narrative_on_principles}</p>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-500 italic">No progress reports submitted yet</p>
            )}
          </div>

          {/* Flags Comparison */}
          {latestReport && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Principles & Red Lines Adherence</Label>
                {!flagsMatch && (
                  <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    Flags Don't Match - Will Dispute
                  </Badge>
                )}
                {flagsMatch && (
                  <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                    Flags Match - Can Validate
                  </Badge>
                )}
              </div>
              <p className="text-xs text-slate-400">
                Review officer's flags and adjust yours. If flags match exactly, you can validate. Otherwise, it will be disputed.
              </p>

              <div className="space-y-2">
                {allItems.map((item, idx) => {
                  const isRedLine = applicableRedLines.includes(item);
                  const reportedFlagged = reportedFlags.includes(item);
                  const validatorFlagged = validatorFlags.includes(item);
                  const flagMismatch = reportedFlagged !== validatorFlagged;

                  return (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg border ${
                        flagMismatch 
                          ? 'bg-amber-500/10 border-amber-500/30' 
                          : 'bg-slate-950/50 border-slate-800'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            {isRedLine ? (
                              <Flame className="w-4 h-4 text-rose-400 flex-shrink-0" />
                            ) : (
                              <Shield className="w-4 h-4 text-indigo-400 flex-shrink-0" />
                            )}
                            <span className="text-sm text-slate-300">{item}</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {/* Officer's flag */}
                          <div className="text-center">
                            <div className="text-xs text-slate-500 mb-1">Officer</div>
                            {reportedFlagged ? (
                              <Badge className="bg-amber-500/30 text-amber-300 border-amber-500/50 text-xs">
                                Struggling
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="border-slate-700 text-slate-500 text-xs">
                                OK
                              </Badge>
                            )}
                          </div>

                          {/* Validator's flag */}
                          <div className="text-center">
                            <div className="text-xs text-slate-500 mb-1">You</div>
                            <button
                              type="button"
                              onClick={() => toggleValidatorFlag(item)}
                              className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
                                validatorFlagged
                                  ? 'bg-amber-500/30 text-amber-300 border border-amber-500/50'
                                  : 'bg-slate-800 text-slate-400 border border-slate-700 hover:bg-slate-700'
                              }`}
                            >
                              {validatorFlagged ? 'Struggling' : 'OK'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Commander validation */}
          <div className="space-y-4">

            <div className="space-y-2">
              <Label>Commander's Notes</Label>
              <Textarea data-testid="cmdr-activityprinciplesvalidation-textarea"
                value={validationForm.notes}
                onChange={(e) => setValidationForm({ ...validationForm, notes: e.target.value })}
                className="bg-slate-950 border-slate-700 min-h-[100px]"
                placeholder="Provide feedback on the officer's adherence to values and principles..." />

            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-emerald-400">
                  <Award className="w-4 h-4" />
                  <Label>Bonuses (Rewards)</Label>
                </div>
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs text-slate-400">XP Bonus</Label>
                    <Input data-testid="cmdr-text-xs-text-slate-400-input"
                      type="number"
                      value={validationForm.xp_bonus_applied}
                      onChange={(e) => setValidationForm({ ...validationForm, xp_bonus_applied: parseInt(e.target.value) || 0 })}
                      className="bg-slate-950 border-slate-700"
                      min="0" />

                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">SP Bonus (Leadership)</Label>
                    <Input data-testid="cmdr-text-xs-text-slate-400-input-1"
                      type="number"
                      value={validationForm.sp_bonus_applied}
                      onChange={(e) => setValidationForm({ ...validationForm, sp_bonus_applied: parseInt(e.target.value) || 0 })}
                      className="bg-slate-950 border-slate-700"
                      min="0" />

                  </div>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2 text-red-400">
                  <AlertTriangle className="w-4 h-4" />
                  <Label>Maluses (Penalties)</Label>
                </div>
                <div className="space-y-2">
                  <div>
                    <Label className="text-xs text-slate-400">XP Malus</Label>
                    <Input data-testid="cmdr-text-xs-text-slate-400-input-2"
                      type="number"
                      value={validationForm.xp_malus_applied}
                      onChange={(e) => setValidationForm({ ...validationForm, xp_malus_applied: parseInt(e.target.value) || 0 })}
                      className="bg-slate-950 border-slate-700"
                      min="0" />

                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">SP Malus (Leadership)</Label>
                    <Input data-testid="cmdr-text-xs-text-slate-400-input-3"
                      type="number"
                      value={validationForm.sp_malus_applied}
                      onChange={(e) => setValidationForm({ ...validationForm, sp_malus_applied: parseInt(e.target.value) || 0 })}
                      className="bg-slate-950 border-slate-700"
                      min="0" />

                  </div>
                </div>
              </div>
            </div>

            <div className="p-3 bg-slate-950/50 rounded-lg border border-slate-800">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-400">Net XP Impact:</span>
                <span className={`font-semibold ${(validationForm.xp_bonus_applied - validationForm.xp_malus_applied) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {(validationForm.xp_bonus_applied - validationForm.xp_malus_applied) >= 0 ? '+' : ''}{validationForm.xp_bonus_applied - validationForm.xp_malus_applied} XP
                </span>
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-sm text-slate-400">Net SP Impact:</span>
                <span className={`font-semibold ${(validationForm.sp_bonus_applied - validationForm.sp_malus_applied) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {(validationForm.sp_bonus_applied - validationForm.sp_malus_applied) >= 0 ? '+' : ''}{validationForm.sp_bonus_applied - validationForm.sp_malus_applied} SP
                </span>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button data-testid="cmdr-cancel-btn" type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button 
              type="submit" 
              className={`flex-1 ${
                flagsMatch 
                  ? 'bg-emerald-600 hover:bg-emerald-700' 
                  : 'bg-amber-600 hover:bg-amber-700'
              }`}
            >
              {flagsMatch ? 'Validate' : 'Dispute'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </ConfirmableDialog>);

}